import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Search, Pencil, Trash2, Flame, Sparkles, Star, X, Upload, ArrowUp, ArrowDown,
} from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";
import { PageTitle, Card, Field, inputCls, GoldButton, GhostButton } from "@/admin/ui";

const empty = {
  name: "", provider: "PG Soft", category: "Slots", image: "",
  hot: false, is_new: false, favorite: false, status: "online",
};

export const GamesManager = () => {
  const [games, setGames] = useState([]);
  const [query, setQuery] = useState("");
  const [prov, setProv] = useState("all");
  const [editing, setEditing] = useState(null); // game or {} for new
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/admin/games").then(({ data }) => setGames(data));
  useEffect(() => { load(); }, []);

  const providers = useMemo(() => Array.from(new Set(games.map((g) => g.provider))), [games]);
  const filtered = useMemo(
    () =>
      games.filter(
        (g) =>
          g.name.toLowerCase().includes(query.toLowerCase()) &&
          (prov === "all" || g.provider === prov),
      ),
    [games, query, prov],
  );

  const openNew = () => { setForm(empty); setEditing({}); };
  const openEdit = (g) => { setForm({ ...g }); setEditing(g); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/games/${editing.id}`, form);
        toast.success("Jogo atualizado");
      } else {
        await api.post("/admin/games", form);
        toast.success("Jogo criado");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Excluir "${g.name}"?`)) return;
    await api.delete(`/admin/games/${g.id}`);
    toast.success("Jogo excluído");
    load();
  };

  const quickToggle = async (g, key) => {
    await api.put(`/admin/games/${g.id}`, { ...g, [key]: !g[key] });
    load();
  };

  const move = async (g, dir) => {
    const ids = games.map((x) => x.id);
    const idx = ids.indexOf(g.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= ids.length) return;
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    setGames((prev) => {
      const map = Object.fromEntries(prev.map((x) => [x.id, x]));
      return ids.map((id, i) => ({ ...map[id], order: i }));
    });
    await api.post("/admin/games/reorder", ids);
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Imagem enviada");
    } catch {
      toast.error("Falha no upload");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="admin-games">
      <PageTitle
        title="Gerenciador de Jogos"
        subtitle={`${games.length} jogos no catálogo`}
        action={<GoldButton onClick={openNew} data-testid="game-add-btn"><Plus className="w-4 h-4 inline mr-1" /> Adicionar Jogo</GoldButton>}
      />

      <Card className="mb-6 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Pesquisar jogo..."
            className={inputCls + " pl-11"}
            data-testid="game-search"
          />
        </div>
        <select value={prov} onChange={(e) => setProv(e.target.value)} className={inputCls + " md:w-56"} data-testid="game-provider-select">
          <option value="all">Todas as Provedoras</option>
          {providers.map((p) => (<option key={p} value={p}>{p}</option>))}
        </select>
      </Card>

      <Card className="!p-0 overflow-hidden">
        <div className="divide-y divide-white/[0.05] max-h-[70vh] overflow-y-auto">
          {filtered.map((g) => (
            <div key={g.id} className="flex items-center gap-3 p-3 hover:bg-white/[0.02] transition-colors" data-testid={`game-row-${g.id}`}>
              {g.image ? (
                <img src={imgUrl(g.image)} alt="" className="w-11 h-14 rounded-lg object-cover border border-white/10 shrink-0" />
              ) : (
                <div className="w-11 h-14 rounded-lg bg-white/5 border border-white/10 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-white truncate">{g.name}</div>
                <div className="text-[11px] text-white/40">{g.provider}</div>
              </div>
              <div className="hidden sm:flex items-center gap-1">
                <IconToggle active={g.hot} onClick={() => quickToggle(g, "hot")} icon={Flame} title="HOT" color="#FF3B30" />
                <IconToggle active={g.is_new} onClick={() => quickToggle(g, "is_new")} icon={Sparkles} title="NOVO" color="#00FF41" />
                <IconToggle active={g.favorite} onClick={() => quickToggle(g, "favorite")} icon={Star} title="Favorito" color="#F5C542" />
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => move(g, -1)} className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5" title="Subir"><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => move(g, 1)} className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5" title="Descer"><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => openEdit(g)} className="p-2 rounded-lg text-white/60 hover:text-[#F5C542] hover:bg-white/5" data-testid={`game-edit-${g.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(g)} className="p-2 rounded-lg text-white/60 hover:text-[#FF3B30] hover:bg-white/5" data-testid={`game-delete-${g.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="p-8 text-center text-white/40">Nenhum jogo.</div>}
        </div>
      </Card>

      {/* Modal */}
      <AnimatePresence>
        {editing && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setEditing(null)} />
            <motion.form
              onSubmit={save}
              initial={{ scale: 0.94, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 16 }}
              className="relative glass-gold w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
              data-testid="game-modal"
            >
              <button type="button" onClick={() => setEditing(null)} className="absolute top-4 right-4 text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
              <h3 className="font-display text-xl font-bold text-white mb-5">
                {editing.id ? "Editar Jogo" : "Novo Jogo"}
              </h3>

              <div className="space-y-4">
                <Field label="Nome">
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="game-form-name" />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Provedora">
                    <input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label="Categoria">
                    <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} />
                  </Field>
                </div>
                <Field label="Imagem (URL ou upload)">
                  <div className="flex gap-2">
                    <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="/games/...jpg" className={inputCls} data-testid="game-form-image" />
                    <label className="shrink-0 flex items-center gap-1.5 px-3 rounded-xl bg-white/[0.04] border border-white/10 text-white/70 hover:text-white cursor-pointer text-sm">
                      <Upload className="w-4 h-4" /> {uploading ? "..." : "Upload"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
                    </label>
                  </div>
                </Field>
                {form.image && (
                  <img src={imgUrl(form.image)} alt="" className="w-20 h-24 rounded-lg object-cover border border-white/10" />
                )}
                <div className="flex flex-wrap gap-4 pt-2">
                  <Check label="HOT" checked={form.hot} onChange={(v) => setForm({ ...form, hot: v })} />
                  <Check label="NOVO" checked={form.is_new} onChange={(v) => setForm({ ...form, is_new: v })} />
                  <Check label="Favorito" checked={form.favorite} onChange={(v) => setForm({ ...form, favorite: v })} />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <GoldButton type="submit" disabled={saving} className="flex-1" data-testid="game-save-btn">
                  {saving ? "Salvando..." : "Salvar"}
                </GoldButton>
                <GhostButton type="button" onClick={() => setEditing(null)}>Cancelar</GhostButton>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const IconToggle = ({ active, onClick, icon: Icon, title, color }) => (
  <button
    onClick={onClick}
    title={title}
    className="p-2 rounded-lg hover:bg-white/5 transition-colors"
    style={{ color: active ? color : "rgba(255,255,255,0.25)" }}
  >
    <Icon className="w-4 h-4" fill={active ? color : "none"} />
  </button>
);

const Check = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 cursor-pointer select-none">
    <span
      onClick={() => onChange(!checked)}
      className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${checked ? "bg-[#F5C542] border-[#F5C542]" : "border-white/20"}`}
    >
      {checked && (
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: "#000" }}>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </span>
    <span className="text-sm text-white/70">{label}</span>
  </label>
);
